// origin: WS_DeskOS artifacts/api-server/src/i18n/index.ts (defineNamespace; EN source instead of RU)
import { DEFAULT_LANG, isLang, lookup, renderEntry, translate, type Dict, type Lang, type Params, type TFunction } from "./core";

export type Namespace<D extends Dict> = {
  /** Typed translator for one language. */
  tFor: (lang: Lang | null | undefined) => TFunction<D>;
  /** For keys built at runtime (error codes, enum values): text or undefined. */
  tryFor: (lang: Lang | null | undefined, key: string, params?: Params) => string | undefined;
};

/**
 * One area's dictionaries, every language imported statically:
 *   export const commonT = defineNamespace(en, { ru, es });
 *   commonT.tFor(lang)("save")
 */
export function defineNamespace<D extends Dict>(source: D, others: { ru: Dict; es: Dict }): Namespace<D> {
  const dicts: Record<Lang, Dict> = { en: source, ru: others.ru, es: others.es };
  const langOrDefault = (lang: Lang | null | undefined): Lang => (isLang(lang) ? lang : DEFAULT_LANG);
  const tFor = (lang: Lang | null | undefined): TFunction<D> => {
    const l = langOrDefault(lang);
    return ((key: string, params?: Params) => translate({ source, current: dicts[l] }, l, key, params)) as unknown as TFunction<D>;
  };
  const tryFor = (lang: Lang | null | undefined, key: string, params?: Params) => {
    const l = langOrDefault(lang);
    const own = l === DEFAULT_LANG ? undefined : lookup(dicts[l], key);
    if (own !== undefined) return renderEntry(own, l, params);
    const base = lookup(source, key);
    return base === undefined ? undefined : renderEntry(base, DEFAULT_LANG, params);
  };
  return { tFor, tryFor };
}
